import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from '@angular/router';
import { AuthService } from '../auth/service/auth.service';
import { Observable, of, from } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { toCamelCase } from '@shared/utils/string-case-manipulation';
import { User } from '../auth/model/interfaces/user.interface';

@Injectable({ providedIn: 'root' })
export class LoginEntryGuard implements CanActivate {
  private authService = inject(AuthService);
  private router = inject(Router);

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    const projectName = route.paramMap.get('project') || '';
    const nextUrlFromQuery = route.queryParamMap.get('next');

    return from(this.authService.isLoggedIn(projectName)).pipe(
      map((response) => {
        if (response.data) {
          this.authService.user = toCamelCase(response.data) as User;

          // Determine redirect URL: query param takes precedence, then localStorage
          let nextUrl = nextUrlFromQuery;

          // CRITICAL: If no 'next' param in URL, check localStorage for SSO redirect URL
          // This handles the case where the backend doesn't preserve RelayState after SAML auth
          // Only check localStorage here (when user is logged in) to avoid clearing it prematurely
          if (!nextUrl) {
            const storedSsoRedirectUrl = this.authService.getAndClearSsoRedirectUrl();
            if (storedSsoRedirectUrl) {
              nextUrl = storedSsoRedirectUrl;
            }
          }

          // If there's a 'next' parameter, redirect to that URL instead of dashboard
          if (nextUrl) {
            // Decode the URL if it's still encoded (handles double-encoding from email links)
            let targetUrl = nextUrl;
            if (targetUrl.includes('%')) {
              try {
                targetUrl = decodeURIComponent(targetUrl);
              } catch {
                // Keep original if decode fails
              }
            }
            // Use navigateTo which handles both Angular and non-Angular routes
            this.authService.navigateTo(targetUrl);
            // Return false to prevent further navigation (navigateTo handles it)
            return false;
          }

          return this.router.createUrlTree(['/dashboard']);
        }
        // User is not logged in - allow navigation to login page
        // Don't clear localStorage here; let login component handle it
        return true;
      }),
      catchError(() => of(true)),
    );
  }
}
